import type { NoteDto } from '@keep/shared';
import { Check, Pin, PinOff } from 'lucide-react';
import { memo, useState } from 'react';
import { usePreferences } from '../../api/auth';
import { useUpdateNote } from '../../api/notes';
import { Checkbox } from '../../components/ui/checkbox';
import { cn } from '../../lib/cn';
import { noteColorVar } from '../../lib/colors';
import { linkify } from '../../lib/linkify';
import { AttachmentGrid } from './attachment-grid';
import { CollaboratorAvatars, NoteChips } from './note-chips';
import { NoteToolbar } from './note-toolbar';
import { useImageUpload } from './use-image-upload';
import type { NoteActions } from './use-note-actions';

const MAX_PREVIEW_ITEMS = 10;
const MAX_PREVIEW_CHARS = 700;

/** Les notes très courtes s'affichent en grand, comme dans Keep. */
function isShortNote(note: NoteDto) {
  return (
    note.type === 'text' &&
    !note.title &&
    note.attachments.length === 0 &&
    note.content.length <= 60 &&
    note.content.split('\n').length <= 3
  );
}

function ChecklistPreview({ note }: { note: NoteDto }) {
  const { moveCheckedToBottom } = usePreferences();
  const update = useUpdateNote();
  const unchecked = moveCheckedToBottom ? note.items.filter((i) => !i.checked) : note.items;
  const checkedCount = moveCheckedToBottom ? note.items.length - unchecked.length : 0;
  const shown = unchecked.slice(0, MAX_PREVIEW_ITEMS);
  const hidden = unchecked.length - shown.length;

  const toggle = (id: string, checked: boolean) =>
    update.mutate({
      id: note.id,
      patch: { items: note.items.map((item) => (item.id === id ? { ...item, checked } : item)) },
    });

  return (
    <div className="flex flex-col gap-0.5">
      {shown.map((item) => (
        <div key={item.id} className="flex items-start gap-2 py-0.5 text-sm">
          <Checkbox
            checked={item.checked}
            label={item.text || 'Élément'}
            onChange={(checked) => toggle(item.id, checked)}
            className={cn('mt-px', note.trashedAt && 'pointer-events-none')}
          />
          <span className={cn('min-w-0 break-words', item.checked && 'text-muted line-through')}>
            {item.text}
          </span>
        </div>
      ))}
      {hidden > 0 && <div className="pl-[26px] text-sm text-muted">…</div>}
      {checkedCount > 0 && (
        <div className="pt-1 pl-[26px] text-sm text-muted">
          + {checkedCount} {checkedCount === 1 ? 'élément coché' : 'éléments cochés'}
        </div>
      )}
    </div>
  );
}

export interface NoteCardProps {
  note: NoteDto;
  actions: NoteActions;
  selected: boolean;
  selectionMode: boolean;
  onOpen: (note: NoteDto) => void;
  onToggleSelect: (note: NoteDto) => void;
  onCollaborators: (note: NoteDto) => void;
  /** Rendu dans l'overlay de glisser-déposer : pas d'interactions. */
  overlay?: boolean;
}

export const NoteCard = memo(function NoteCard({
  note,
  actions,
  selected,
  selectionMode,
  onOpen,
  onToggleSelect,
  onCollaborators,
  overlay,
}: NoteCardProps) {
  const [menuOpen, setMenuOpen] = useState(false);
  const uploadImages = useImageUpload();
  const trashed = note.trashedAt !== null;
  const content =
    note.content.length > MAX_PREVIEW_CHARS
      ? `${note.content.slice(0, MAX_PREVIEW_CHARS)}…`
      : note.content;
  const empty =
    !note.title && !note.content && note.items.length === 0 && note.attachments.length === 0;

  return (
    <article
      aria-label={note.title || 'Note'}
      tabIndex={0}
      onClick={() => (selectionMode ? onToggleSelect(note) : onOpen(note))}
      onKeyDown={(event) => {
        if (event.key === 'Enter' && event.target === event.currentTarget) onOpen(note);
      }}
      style={{ backgroundColor: noteColorVar(note.color) }}
      className={cn(
        'group relative flex cursor-default flex-col rounded-lg border text-fg transition-shadow duration-150 outline-none select-none',
        'hover:shadow-card focus-visible:shadow-card',
        selected
          ? 'border-fg ring-1 ring-fg'
          : note.color === 'default'
            ? 'border-line'
            : 'border-transparent',
        overlay && 'shadow-dialog',
      )}
    >
      {/* Sélection multiple : coche en haut à gauche. */}
      {!overlay && (
        <button
          type="button"
          aria-label={selected ? 'Désélectionner la note' : 'Sélectionner la note'}
          onClick={(event) => {
            event.stopPropagation();
            onToggleSelect(note);
          }}
          className={cn(
            'absolute -top-2.5 -left-2.5 z-10 flex size-6 items-center justify-center rounded-full bg-fg text-bg transition-opacity',
            selected || menuOpen
              ? 'opacity-100'
              : 'opacity-0 group-focus-within:opacity-100 group-hover:opacity-100',
          )}
        >
          <Check size={16} strokeWidth={3} />
        </button>
      )}

      {!trashed && !overlay && (
        <button
          type="button"
          aria-label={note.pinned ? 'Retirer la note' : 'Épingler la note'}
          title={note.pinned ? 'Retirer la note' : 'Épingler la note'}
          onClick={(event) => {
            event.stopPropagation();
            actions.setPinned([note], !note.pinned);
          }}
          className={cn(
            'absolute top-1 right-1 z-10 flex size-9 items-center justify-center rounded-full text-muted hover:bg-hover hover:text-fg',
            'opacity-0 transition-opacity group-focus-within:opacity-100 group-hover:opacity-100',
            menuOpen && 'opacity-100',
          )}
        >
          {note.pinned ? <PinOff size={20} /> : <Pin size={20} />}
        </button>
      )}

      <AttachmentGrid attachments={note.attachments} maxRows={2} className="rounded-t-[7px]" />

      <div
        className={cn(
          'flex flex-col gap-2 px-4 pt-3',
          note.title || note.attachments.length ? 'pb-1' : 'pb-2',
        )}
      >
        {note.title && (
          <h2 className="pr-6 text-base leading-6 font-medium break-words whitespace-pre-wrap">
            {note.title}
          </h2>
        )}
        {note.type === 'checklist'
          ? note.items.length > 0 && <ChecklistPreview note={note} />
          : note.content && (
              <p
                className={cn(
                  'break-words whitespace-pre-wrap',
                  isShortNote(note)
                    ? 'pr-4 text-[22px] leading-7'
                    : 'text-sm leading-5 tracking-[0.01em]',
                )}
              >
                {linkify(content)}
              </p>
            )}
        {empty && <p className="text-[22px] leading-7 text-muted">Note vide</p>}
        <NoteChips
          note={note}
          onRemoveReminder={trashed ? undefined : () => actions.setReminder(note, null)}
          onRemoveLabel={
            trashed ? undefined : (labelId) => actions.setLabel([note], labelId, false)
          }
        />
        <CollaboratorAvatars note={note} />
      </div>

      <NoteToolbar
        className={cn(
          'mt-auto px-1 pb-1 transition-opacity duration-150',
          // Sur mobile, comme dans Keep, les actions passent par l'éditeur (cartes trop étroites).
          'max-md:hidden',
          menuOpen
            ? 'opacity-100'
            : 'opacity-0 group-focus-within:opacity-100 group-hover:opacity-100',
        )}
        color={note.color}
        reminderAt={note.reminderAt}
        labelIds={note.labelIds}
        archived={note.archived}
        type={note.type}
        trashed={trashed}
        onMenuOpenChange={setMenuOpen}
        onReminder={(value) => actions.setReminder(note, value)}
        onCollaborators={() => onCollaborators(note)}
        onColor={(color) => actions.setColor([note], color)}
        onAddImages={(files) => uploadImages(note.id, files)}
        onArchive={() => (note.archived ? actions.unarchive([note]) : actions.archive([note]))}
        onTrash={() => actions.trash([note])}
        onToggleLabel={(labelId, enabled) => actions.setLabel([note], labelId, enabled)}
        onCopy={() => actions.copy([note])}
        onRestore={() => actions.restore([note])}
        onDeleteForever={() => actions.deleteForever([note])}
      />
    </article>
  );
});
